import {
  Box,
  Typography,
  Divider,
  Collapse,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Chip,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { useEffect, useState } from 'react';
import { RoleControllerAction, RoleControllerState } from '../../controller/role.controller';
import CloseButton from '../../../../components/closebutton/CloseButton';
import { PermissionModel } from '../../model/role.model';

/**
 * Every seeded permission uses module "mseg", so grouping by `module` gives one
 * flat list. The resource segment of the code (`mseg:role:create` -> `role`)
 * is what actually separates them.
 */
const resourceOf = (permission: PermissionModel) => {
  const parts = (permission.code ?? '').split(':');
  return parts.length > 1 ? parts[1] : permission.module || 'other';
};

const RoleModalPermissions = ({ ctrl }: { ctrl: RoleControllerState & RoleControllerAction }) => {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (ctrl.allPermissions.length === 0) {
      ctrl.loadAllPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const role = ctrl.selectedRole;
  if (!role) return null;

  const keyword = search.trim().toLowerCase();
  const grouped = ctrl.allPermissions
    .filter(
      (p) =>
        keyword === '' ||
        p.name.toLowerCase().includes(keyword) ||
        p.code.toLowerCase().includes(keyword)
    )
    .reduce<Record<string, PermissionModel[]>>((acc, p) => {
      const key = resourceOf(p);
      if (!acc[key]) acc[key] = [];
      acc[key].push(p);
      return acc;
    }, {});

  const draft = ctrl.draftPermissionIds;
  const assignedCount = Object.keys(ctrl.assignedPermissions).length;
  const changedCount =
    draft.filter((id) => ctrl.assignedPermissions[id] === undefined).length +
    Object.keys(ctrl.assignedPermissions)
      .map(Number)
      .filter((id) => !draft.includes(id)).length;
  const busy = ctrl.permissionLoading || ctrl.savingPermissions;

  return (
    <Box sx={{ width: '100%' }}>
      <Box padding={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography fontSize="20px">ຈັດການສິດທິ</Typography>
            <Typography fontSize="13px" color="text.secondary">
              {role.role_name} · ລະດັບ {role.level} · ມີສິດທິ {assignedCount} ລາຍການ
            </Typography>
          </Box>
          <CloseButton onClick={() => ctrl.onHandleModal(null)} />
        </Box>

        <Divider sx={{ backgroundColor: '#1A1A1A20', mt: 1.5, mb: 2 }} />

        <Collapse unmountOnExit in={!!ctrl.message}>
          <Alert severity="error" sx={{ mb: 1 }}>
            {ctrl.message}
          </Alert>
        </Collapse>

        <TextField
          fullWidth
          size="small"
          placeholder="ຄົ້ນຫາສິດທິ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {ctrl.permissionLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        <Box sx={{ maxHeight: '440px', overflowY: 'auto' }}>
          {Object.entries(grouped).map(([resource, perms]) => {
            const ids = perms.map((p) => p.id);
            const selectedInGroup = ids.filter((id) => draft.includes(id));
            return (
              <Box key={resource} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Checkbox
                    size="small"
                    disabled={busy}
                    checked={selectedInGroup.length === ids.length}
                    indeterminate={
                      selectedInGroup.length > 0 && selectedInGroup.length < ids.length
                    }
                    onChange={(e) => ctrl.onToggleDraftGroup(ids, e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                  <Chip
                    label={resource}
                    size="small"
                    sx={{ backgroundColor: '#E4E8F3', fontWeight: 600 }}
                  />
                  <Typography fontSize="12px" color="text.secondary">
                    {selectedInGroup.length} / {ids.length} ເລືອກແລ້ວ
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 0.5,
                    pl: 1,
                  }}
                >
                  {perms.map((p) => (
                    <FormControlLabel
                      key={p.id}
                      disabled={busy}
                      control={
                        <Checkbox
                          checked={draft.includes(p.id)}
                          size="small"
                          onChange={() => ctrl.onToggleDraftPermission(p.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography fontSize="13px">{p.name}</Typography>
                          <Typography fontSize="11px" color="text.secondary">
                            {p.code}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </Box>
              </Box>
            );
          })}

          {Object.keys(grouped).length === 0 && !ctrl.permissionLoading && (
            <Typography color="text.secondary" textAlign="center" py={3}>
              ບໍ່ພົບສິດທິທີ່ຄົ້ນຫາ
            </Typography>
          )}
        </Box>

        <Divider sx={{ backgroundColor: '#1A1A1A20', mt: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Typography fontSize="13px" color="text.secondary">
            {changedCount > 0 ? `ມີການປ່ຽນແປງ ${changedCount} ລາຍການ` : 'ຍັງບໍ່ມີການປ່ຽນແປງ'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => ctrl.onHandleModal(null)}
            sx={{ borderRadius: '100px', height: '41px', minWidth: '140px' }}
            variant="outlined"
            size="small"
          >
            ຍົກເລີກ
          </Button>
          <Button
            disabled={busy || changedCount === 0}
            onClick={() => ctrl.onSaveRolePermissions()}
            sx={{ borderRadius: '100px', height: '41px', minWidth: '140px' }}
            size="small"
            variant="contained"
            endIcon={
              ctrl.savingPermissions ? (
                <CircularProgress size="16px" sx={{ color: 'white' }} />
              ) : (
                <CheckIcon />
              )
            }
          >
            ບັນທຶກ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default RoleModalPermissions;
